//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Read Only component
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   13.04.21 Sean Flook          WI39345 Initial Revision.
//    002   05.05.21 Sean Flook          WI39345 Tweaks to the UI after design review meeting.
//    003   14.05.21 Sean Flook          WI39345 Updated className.
//    004   27.10.23 Sean Flook                  Updated call to FormRowStyle.
//    005   27.02.24 Sean Flook            MUL16 Added ability to display a button.
//    006   18.03.24 Sean Flook       STRFRM4_OS Added nullString parameter.
//    007   29.05.24 Sean Flook        IMANN-490 Modified for USRN.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    008   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//endregion Version 1.0.5.0
//region Version 1.0.7.0
//    009   13.06.25 Sean Flook       IMANN-1812 Moved defaults.
//    010   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    011   24.10.25 Sean Flook       IMANN-1129 Added the remove button variant and fixed the height of the control.
//    012   05.12.25 Sean Flook       IMANN-1671 If we have a button increase the height of the control.
//    013   02.02.26 Sean Flook       IMANN-2274 Set the box height to auto.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

//region imports

import React, { useState, useRef, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import { Button, Grid, Typography, Skeleton, Tooltip, Stack, Box } from "@mui/material";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";

import {
  FormRowStyle,
  controlLabelStyle,
  boldControlLabelStyle,
  transparentButtonStyle,
  tooltipStyle,
  FormBoxRowStyle,
} from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";
import ADSErrorDisplay from "./ADSErrorDisplay";

//endregion imports

ADSReadOnlyControl.propTypes = {
  loading: PropTypes.bool,
  noLeftPadding: PropTypes.bool,
  boldLabel: PropTypes.bool,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  nullString: PropTypes.string,
  buttonVariant: PropTypes.oneOf(["none", "viewRelated", "edit", "removeFile"]),
  errorText: PropTypes.array,
  onButtonClick: PropTypes.func,
};

function ADSReadOnlyControl({
  loading = false,
  noLeftPadding = false,
  boldLabel = false,
  label,
  value,
  nullString = "",
  buttonVariant = "none",
  errorText,
  onButtonClick,
}) {
  const theme = useTheme();

  const [displayError, setDisplayError] = useState("");
  const hasError = useRef(false);

  /**
   * Event to handle the button click
   */
  const handleButtonClick = () => {
    if (onButtonClick) onButtonClick();
  };

  /**
   * Method to get the styling for the control.
   *
   * @returns {object} The styling to use on the control.
   */
  const getValueStyle = () => {
    if (noLeftPadding) return null;
    else
      return {
        pl: theme.spacing(2),
      };
  };

  const getButtonTooltip = () => {
    switch (buttonVariant) {
      case "viewRelated":
        return "View all related records";

      case "edit":
        return `Edit ${label.replaceAll("*", "")}`;

      case "removeFile":
        return `Remove ${label.replaceAll("*", "")}`;

      default:
        return "";
    }
  };

  /**
   * Method to get the button icon.
   *
   * @returns {JSX.Element || null} The icon to be used on the button.
   */
  const getButtonIcon = () => {
    switch (buttonVariant) {
      case "viewRelated":
        return <ArrowForwardIcon />;

      case "edit":
        return <EditIcon />;

      case "removeFile":
        return <CancelIcon />;

      default:
        return null;
    }
  };

  const getButtonText = () => {
    switch (buttonVariant) {
      case "viewRelated":
        return "All related";

      case "edit":
        return `Edit ${label.replaceAll("*", "")}`;

      case "removeFile":
        return `Remove`;

      default:
        return "";
    }
  };

  const getButton = () => {
    switch (buttonVariant) {
      case "viewRelated":
        return (
          <Fragment>
            <Grid size={9}>
              <Typography
                id={`${label.toLowerCase().replaceAll(" ", "-")}-read-only`}
                variant="body1"
                align="left"
                color="textPrimary"
                sx={getValueStyle()}
                aria-labelledby={`${label.toLowerCase().replaceAll(" ", "-")}-label`}
              >
                {loading ? <Skeleton animation="wave" /> : value ? value : nullString}
              </Typography>
            </Grid>
            <Grid size={3} />
            <Grid size={9}>
              <Tooltip title={getButtonTooltip()} arrow placement="right" sx={tooltipStyle}>
                <Button
                  variant="contained"
                  onClick={handleButtonClick}
                  sx={{ ...transparentButtonStyle, pt: "0px", pb: "0px" }}
                  startIcon={getButtonIcon()}
                >
                  {getButtonText()}
                </Button>
              </Tooltip>
            </Grid>
          </Fragment>
        );

      case "edit":
        return (
          <Fragment>
            <Grid size={9}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  id={`${label.toLowerCase().replaceAll(" ", "-")}-read-only`}
                  variant="body1"
                  align="left"
                  color="textPrimary"
                  sx={getValueStyle()}
                  aria-labelledby={`${label.toLowerCase().replaceAll(" ", "-")}-label`}
                >
                  {loading ? <Skeleton animation="wave" /> : value ? value : nullString}
                </Typography>
                <Tooltip title={getButtonTooltip()} arrow placement="right" sx={tooltipStyle}>
                  <Button
                    variant="contained"
                    onClick={handleButtonClick}
                    sx={{ ...transparentButtonStyle, pt: "0px", pb: "0px" }}
                    startIcon={getButtonIcon()}
                  >
                    {getButtonText()}
                  </Button>
                </Tooltip>
              </Stack>
            </Grid>
          </Fragment>
        );

      case "removeFile":
        return (
          <Fragment>
            <Grid size={9}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography
                  id={`${label.toLowerCase().replaceAll(" ", "-")}-read-only`}
                  variant="body1"
                  align="left"
                  color="textPrimary"
                  sx={getValueStyle()}
                  aria-labelledby={`${label.toLowerCase().replaceAll(" ", "-")}-label`}
                >
                  {loading ? <Skeleton animation="wave" /> : value ? value : nullString}
                </Typography>
                <Tooltip title={getButtonTooltip()} arrow placement="right" sx={tooltipStyle}>
                  <Button
                    variant="contained"
                    onClick={handleButtonClick}
                    sx={{ ...transparentButtonStyle, pt: "0px", pb: "0px" }}
                    startIcon={getButtonIcon()}
                  >
                    {getButtonText()}
                  </Button>
                </Tooltip>
              </Stack>
            </Grid>
          </Fragment>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    hasError.current = errorText && errorText.length > 0;

    if (hasError.current) {
      if (Array.isArray(errorText)) setDisplayError(errorText.join(", "));
      else setDisplayError(errorText);
    } else setDisplayError(null);
  }, [errorText]);

  return (
    <Box sx={{ ...FormBoxRowStyle(hasError.current), height: "Auto" }}>
      <Grid container justifyContent="flex-start" alignItems="center" sx={FormRowStyle(false)}>
        <Grid size={3}>
          <Typography
            id={`${label.toLowerCase().replaceAll(" ", "-")}-label`}
            variant="body2"
            align="left"
            color="textPrimary"
            sx={boldLabel ? boldControlLabelStyle : controlLabelStyle}
          >
            {label}
          </Typography>
        </Grid>
        {buttonVariant === "none" ? (
          <Grid size={9}>
            <Typography
              id={`${label.toLowerCase().replaceAll(" ", "-")}-read-only`}
              variant="body1"
              align="left"
              color="textPrimary"
              sx={getValueStyle()}
              aria-labelledby={`${label.toLowerCase().replaceAll(" ", "-")}-label`}
            >
              {loading ? <Skeleton animation="wave" /> : value ? value : nullString}
            </Typography>
          </Grid>
        ) : (
          getButton()
        )}
        <ADSErrorDisplay errorText={displayError} id={`${label.toLowerCase().replaceAll(" ", "-")}-read-only-error`} />
      </Grid>
    </Box>
  );
}

export default ADSReadOnlyControl;
